import dayjs from "dayjs";
import { PopupHoverContainer } from "@similarweb/ui-components";
import { DefaultCell, CheckMarkCell } from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { translate } from "app/filters/i18nFilter";
import {
    COLUMN_CATEGORY,
    COLUMN_DESCRIPTION,
    COLUMN_INSTALLATION_DATE,
    COLUMN_IS_FREE,
    COLUMN_IS_PAID,
    COLUMN_REMOVED_PERIOD,
    COLUMN_STATUS,
    COLUMN_SUB_CATEGORY,
    COLUMN_TECHNOLOGY_NAME,
    COLUMN_WORKING_PERIOD,
} from "app/pages/website-analysis/website-technologies/constants/column-names";
import { DomainNameAndIcon } from "app/pages/workspace/common/tableAdditionalColumns";
import dateTimeService from "app/services/date-time/dateTimeService";
import { WHITESPACE_MMMM_YYYY } from "app/services/date-time/formats";
import { CheckMark } from "./components/CheckMark";
import { DomainFaviconHeader } from "./components/DomainFaviconHeader";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import type { TechnologyItem } from "app/pages/workspace/sales/sub-modules/feed/types/technologies";
import { DescriptionPopup } from "./components/DescriptionPopup";
import { SubdomainsPopupContent } from "./styles";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

const domainCheckedColumn = (site: string, icon: string) => ({
    field: site,
    sortable: false,
    isSorted: false,
    cellComponent: ({ value }: { value: boolean }) => <CheckMark isChecked={value} />,
    headerComponent: ({ field }: { field: string }) => (
        <DomainFaviconHeader
            domain={field}
            icon={icon}
        />
    ),
    visible: true,
    cellClass: "u-no-padding",
    isResizable: false,
    width: 48,
});

export function getTableColumns(
    keys: string[],
    order: Record<string, string>,
    getSiteImage: (key: string) => string,
    selectedSubdomains: string[],
) {
    const [[sortField, sortDirection]] = Object.entries(order);
    const isSingleMode = keys.length === 1;

    function isSortedBy(field: string) {
        return field === sortField;
    }

    function getSortDirection(field: string) {
        return isSortedBy(field) ? sortDirection : "asc";
    }

    function hideColumn(column: { field: string }) {
        const columnsHiddenInCompareMode = [
            COLUMN_STATUS,
            COLUMN_WORKING_PERIOD,
            COLUMN_INSTALLATION_DATE,
            COLUMN_REMOVED_PERIOD,
        ];
        const columnsHiddenInSingleMode = [COLUMN_DESCRIPTION];
        const hiddenColumns = isSingleMode ? columnsHiddenInSingleMode : columnsHiddenInCompareMode;

        return { ...column, visible: !hiddenColumns.includes(column.field) };
    }

    const singleColumns = [
        {
            field: COLUMN_TECHNOLOGY_NAME,
            fixed: true,
            displayName: translate("analysis.overview.technographics.table.technology_name"),
            showTotalCount: true,
            isSorted: isSortedBy(COLUMN_TECHNOLOGY_NAME),
            sortDirection: getSortDirection(COLUMN_TECHNOLOGY_NAME),
            sortable: true,
            visible: true,
            headerComponent: DefaultCellHeader,
            cellComponent: ({ row }: { row: TechnologyItem }) => (
                <DescriptionPopup
                    description={row.description}
                    enabled={isSingleMode}
                >
                    <DomainNameAndIcon
                        domain={row.technologyName}
                        icon={salesSettingsHelper.getTechnographicsLink(row.technologyId)}
                    />
                </DescriptionPopup>
            ),
            width: 261,
        },
        {
            field: COLUMN_CATEGORY,
            displayName: translate(`analysis.overview.technographics.table.category`),
            sortable: true,
            isSorted: isSortedBy(COLUMN_CATEGORY),
            cellComponent: DefaultCell,
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_CATEGORY),
            width: 185,
            visible: true,
        },
        {
            field: COLUMN_SUB_CATEGORY,
            displayName: translate("analysis.overview.technographics.table.sub_category"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_SUB_CATEGORY),
            sortDirection: getSortDirection(COLUMN_SUB_CATEGORY),
            cellComponent: DefaultCell,
            headerComponent: DefaultCellHeader,
            width: 156,
            visible: true,
        },
        {
            field: COLUMN_STATUS,
            displayName: translate("analysis.overview.technographics.table.status"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_STATUS),
            cellComponent: (props: { row: TechnologyItem; value: "installed" | "removed" }) => {
                const isRemoved = props.row.status === "removed";

                const subdomains = props.row.usages
                    .filter((usage) => (isRemoved ? !!usage.removalDate : !usage.removalDate))
                    .map((usage) => usage.site);

                const value = translate(`analysis.overview.technographics.table.${props.value}`);

                return (
                    <PopupHoverContainer
                        content={() => <SubdomainsPopupContent>{subdomains.join("\n")}</SubdomainsPopupContent>}
                        config={{ placement: "top", enabled: selectedSubdomains.length > 1 }}
                    >
                        <div>{value}</div>
                    </PopupHoverContainer>
                );
            },
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_STATUS),
            visible: true,
            width: 110,
        },
        {
            field: COLUMN_WORKING_PERIOD,
            displayName: translate("analysis.overview.technographics.table.workingPeriod"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_WORKING_PERIOD),
            cellComponent: ({ value }: { value: number }) => {
                return <span data-automation-cell-value={value}>{dayjs().subtract(value, "ms").fromNow(true)}</span>;
            },
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_WORKING_PERIOD),
            visible: true,
            width: 128,
        },
        {
            field: COLUMN_INSTALLATION_DATE,
            displayName: translate("analysis.overview.technographics.table.installationDate"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_INSTALLATION_DATE),
            cellComponent: ({ value }: { value: number }) => {
                const formattedInstallationDate = dateTimeService.formatWithMoment(value, WHITESPACE_MMMM_YYYY);

                return <span data-automation-cell-value={value}>{formattedInstallationDate}</span>;
            },
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_INSTALLATION_DATE),
            visible: true,
            width: 128,
        },
        {
            field: COLUMN_REMOVED_PERIOD,
            displayName: translate("analysis.overview.technographics.table.removed"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_REMOVED_PERIOD),
            cellComponent: ({ value }: { value: number }) => {
                return (
                    <span data-automation-cell-value={value}>
                        {value === 0 ? "-" : dayjs().subtract(value, "ms").fromNow()}
                    </span>
                );
            },
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_REMOVED_PERIOD),
            visible: true,
            width: 128,
        },
        {
            field: COLUMN_IS_FREE,
            displayName: translate("analysis.overview.technographics.table.free"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_IS_FREE),
            cellComponent: CheckMarkCell,
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_IS_FREE),
            width: 70,
            visible: true,
            isResizable: false,
        },
        {
            field: COLUMN_IS_PAID,
            displayName: translate("analysis.overview.technographics.table.paid"),
            sortable: true,
            isSorted: isSortedBy(COLUMN_IS_PAID),
            cellComponent: CheckMarkCell,
            headerComponent: DefaultCellHeader,
            sortDirection: getSortDirection(COLUMN_IS_PAID),
            width: 70,
            visible: true,
            isResizable: false,
        },
        {
            field: COLUMN_DESCRIPTION,
            displayName: translate("analysis.overview.technographics.table.description"),
            cellClass: "line-clamp-2",
            cellComponent: (props: ITableCellProps) => (
                <DescriptionPopup description={props.value}>
                    <DefaultCell {...props} />
                </DescriptionPopup>
            ),
            headerComponent: DefaultCellHeader,
            isSorted: false,
            minWidth: 300,
            visible: true,
            sortable: false,
        },
    ].map(hideColumn);

    if (isSingleMode) {
        return singleColumns;
    }

    const [technologyColumn, ...other] = singleColumns;
    const hasTechnologyCollection = keys.map((key) => domainCheckedColumn(key, getSiteImage(key)));
    return [technologyColumn, ...hasTechnologyCollection, ...other];
}
